/**
 * Verification Banner — EMAIL VERIFICATION ONLY
 *
 * Cross-cutting top-of-app reminder for users who have not verified their email.
 * Identity / KYC has its own dashboard-scoped reminder (KycReminderBanner) so
 * we never nag a logged-in reader on /blog or /help with KYC copy.
 *
 * Email verification is genuinely global because nearly every authenticated
 * action (donating with receipts, creating campaigns, settings) depends on it.
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  X,
  AlertCircle,
  Mail,
  Clock,
  ChevronDown,
  Sparkles
} from 'lucide-react';
import { useLocaleNavigate } from '@lib/i18n/exports';
import { useAuth } from '../../../src/hooks/useAuth';
import {
  useEmailVerificationStatus,
  useResendVerificationEmail
} from '../../../entities/user/hooks/useProfile';
import { useToastActions } from '../toast/Toast';
import {
  useCaptchaContext,
  isCaptchaChallenge,
  type CaptchaChallenge
} from '../../../lib/captcha';
import { profileApi } from '../../../entities/user/services/profileApi';

interface VerificationBannerProps {
  className?: string;
}

const STAGE = 'email';
const DISMISSAL_KEY = `verification_banner_dismissed_${STAGE}`;
const DISMISSAL_TTL_MS = 24 * 60 * 60 * 1000;

function isBannerDismissed(): boolean {
  const dismissed = localStorage.getItem(DISMISSAL_KEY);
  if (!dismissed) return false;
  const dismissedTime = parseInt(dismissed, 10);
  return Date.now() - dismissedTime < DISMISSAL_TTL_MS;
}

function dismissBanner(): void {
  localStorage.setItem(DISMISSAL_KEY, Date.now().toString());
}

function formatCountdown(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

interface CountdownTimerProps {
  seconds: number;
  totalSeconds: number;
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({
  seconds,
  totalSeconds
}) => {
  const progress = totalSeconds > 0 ? seconds / totalSeconds : 0;
  const size = 36;
  const strokeWidth = 3;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - progress);

  const isUrgent = seconds <= 30;
  const isAlmostReady = seconds <= 10;

  return (
    <motion.div
      className="relative inline-flex items-center justify-center"
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <svg width={size} height={size} className="-rotate-90 transform">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-gray-200 dark:text-gray-600"
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className={`transition-colors duration-300 ${
            isAlmostReady
              ? 'text-emerald-500'
              : isUrgent
                ? 'text-amber-500'
                : 'text-gray-400'
          }`}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        />
      </svg>

      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          animate={isAlmostReady ? { scale: [1, 1.2, 1] } : {}}
          transition={{ duration: 0.5, repeat: isAlmostReady ? Infinity : 0 }}
        >
          <Clock
            className={`h-4 w-4 ${
              isAlmostReady
                ? 'text-emerald-500'
                : isUrgent
                  ? 'text-amber-500'
                  : 'text-gray-400'
            }`}
          />
        </motion.div>
      </div>
    </motion.div>
  );
};

interface InteractiveCountdownProps {
  seconds: number;
  totalSeconds: number;
  attemptsRemaining?: number;
}

const InteractiveCountdown: React.FC<InteractiveCountdownProps> = ({
  seconds,
  totalSeconds,
  attemptsRemaining
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isUrgent = seconds <= 30;
  const isAlmostReady = seconds <= 10;
  const progressPercent =
    totalSeconds > 0 ? Math.round((1 - seconds / totalSeconds) * 100) : 0;

  const getCardStyles = (): string => {
    if (isAlmostReady) {
      return 'border-emerald-300 bg-linear-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 hover:border-emerald-400 hover:shadow-emerald-100';
    }
    if (isUrgent) {
      return 'border-amber-200 bg-linear-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 hover:border-amber-300 hover:shadow-amber-100';
    }
    return 'border-gray-200 bg-linear-to-r from-gray-50 to-slate-50 dark:from-gray-800 dark:to-slate-800 hover:border-gray-300 hover:shadow-gray-100';
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      className="relative"
    >
      <motion.button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className={`
          flex cursor-pointer items-center gap-3 rounded-xl border-2 px-4 py-2.5 shadow-xs transition-all
          duration-300 hover:shadow-md
          ${getCardStyles()}
        `}
        aria-expanded={isExpanded}
        aria-label={`Countdown timer: ${formatCountdown(seconds)} until you can resend. Click to ${isExpanded ? 'collapse' : 'expand'} details.`}
      >
        <CountdownTimer seconds={seconds} totalSeconds={totalSeconds} />

        <div className="flex flex-col items-start">
          <span
            className={`font-mono text-sm font-bold tracking-tight ${
              isAlmostReady
                ? 'text-emerald-700 dark:text-emerald-300'
                : isUrgent
                  ? 'text-amber-700 dark:text-amber-300'
                  : 'text-gray-700 dark:text-gray-200'
            }`}
          >
            {formatCountdown(seconds)}
          </span>
          <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
            until resend
          </span>
        </div>

        <motion.div
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="ml-1"
        >
          <ChevronDown
            className={`h-4 w-4 ${
              isAlmostReady
                ? 'text-emerald-500'
                : isUrgent
                  ? 'text-amber-500'
                  : 'text-gray-400'
            }`}
          />
        </motion.div>
      </motion.button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0, y: -10 }}
            animate={{ opacity: 1, height: 'auto', y: 0 }}
            exit={{ opacity: 0, height: 0, y: -10 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="absolute left-0 right-0 top-full z-50 mt-2"
          >
            <div
              className={`
              rounded-xl border-2 p-4 shadow-lg backdrop-blur-sm
              ${
                isAlmostReady
                  ? 'border-emerald-200 bg-white/95 dark:bg-gray-800/95'
                  : isUrgent
                    ? 'border-amber-200 bg-white/95 dark:bg-gray-800/95'
                    : 'border-gray-200 bg-white/95 dark:bg-gray-800/95'
              }
            `}
            >
              <div className="mb-3">
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                    Cooldown Progress
                  </span>
                  <span
                    className={`text-xs font-bold ${
                      isAlmostReady
                        ? 'text-emerald-600'
                        : isUrgent
                          ? 'text-amber-600'
                          : 'text-gray-500'
                    }`}
                  >
                    {progressPercent}%
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                  <motion.div
                    className={`h-full rounded-full ${
                      isAlmostReady
                        ? 'bg-linear-to-r from-emerald-400 to-teal-500'
                        : isUrgent
                          ? 'bg-linear-to-r from-amber-400 to-orange-500'
                          : 'bg-linear-to-r from-gray-300 to-slate-400'
                    }`}
                    initial={{ width: 0 }}
                    animate={{ width: `${progressPercent}%` }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                  />
                </div>
              </div>

              <div className="mb-3 grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-gray-50 p-2 dark:bg-gray-700/50">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Time Left
                  </span>
                  <p
                    className={`font-mono text-lg font-bold ${
                      isAlmostReady
                        ? 'text-emerald-600'
                        : isUrgent
                          ? 'text-amber-600'
                          : 'text-gray-700 dark:text-gray-200'
                    }`}
                  >
                    {formatCountdown(seconds)}
                  </p>
                </div>
                <div className="rounded-lg bg-gray-50 p-2 dark:bg-gray-700/50">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Resends Left
                  </span>
                  <p
                    className={`text-lg font-bold ${
                      attemptsRemaining === 0
                        ? 'text-rose-600'
                        : attemptsRemaining === 1
                          ? 'text-amber-600'
                          : 'text-gray-700 dark:text-gray-200'
                    }`}
                  >
                    {attemptsRemaining ?? '-'}
                  </p>
                </div>
              </div>

              {isAlmostReady ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-center gap-2 rounded-lg bg-emerald-50 p-2 dark:bg-emerald-900/30"
                >
                  <Sparkles className="h-4 w-4 text-emerald-500" />
                  <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
                    Almost ready! You can resend soon.
                  </span>
                </motion.div>
              ) : (
                <div className="flex items-center gap-2 rounded-lg bg-gray-50 p-2 dark:bg-gray-700/50">
                  <Clock className="h-4 w-4 text-gray-400" />
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    Rate limiting protects against spam. Please wait.
                  </span>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/**
 * Defensive accessor — backend has shipped both flat and nested response shapes
 * for email verification status. Tolerate both rather than chase the wire shape.
 */
function getEmailVerifiedStatus(emailStatus: {
  data?: {
    emailVerified?: boolean;
    data?: {
      emailVerified?: boolean;
    };
  } | null;
}): boolean {
  if (!emailStatus.data) return false;
  const data = emailStatus.data;
  if (typeof data.emailVerified === 'boolean') return data.emailVerified;
  if (
    data.data &&
    typeof data.data === 'object' &&
    typeof data.data.emailVerified === 'boolean'
  ) {
    return data.data.emailVerified;
  }
  return false;
}

export const VerificationBanner: React.FC<VerificationBannerProps> = ({
  className = ''
}) => {
  const { authState } = useAuth();
  const navigate = useLocaleNavigate();
  const toast = useToastActions();
  const emailStatus = useEmailVerificationStatus();
  const { isPending: isResending } = useResendVerificationEmail();

  const { showChallenge } = useCaptchaContext();
  const MAX_CAPTCHA_ATTEMPTS = 3;

  const [dismissed, setDismissed] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [initialCountdown, setInitialCountdown] = useState(0);
  const [isResendingWithCaptcha, setIsResendingWithCaptcha] = useState(false);

  const emailVerified = getEmailVerifiedStatus(emailStatus);

  useEffect((): (() => void) | void => {
    if (
      emailStatus.data?.resendAvailableIn &&
      emailStatus.data.resendAvailableIn > 0
    ) {
      const initialValue = emailStatus.data.resendAvailableIn;
      setCountdown(initialValue);
      setInitialCountdown(prev => (prev < initialValue ? initialValue : prev));

      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            emailStatus.refetch();
            setInitialCountdown(0);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    } else {
      setCountdown(0);
    }
  }, [emailStatus.data?.resendAvailableIn, emailStatus.refetch, emailStatus]);

  useEffect(() => {
    if (isBannerDismissed()) {
      setDismissed(true);
    }
  }, []);

  const handleResendEmail = async (): Promise<void> => {
    setIsResendingWithCaptcha(true);
    let hcaptchaToken: string | undefined;
    let attempts = 0;

    try {
      while (attempts < MAX_CAPTCHA_ATTEMPTS) {
        const response =
          await profileApi.resendVerificationEmail(hcaptchaToken);

        if (isCaptchaChallenge(response)) {
          try {
            hcaptchaToken = await showChallenge(response as CaptchaChallenge);
            attempts++;
            continue;
          } catch {
            toast.error(
              'Verification Cancelled',
              'Security verification was cancelled. Please try again.'
            );
            return;
          }
        }

        if ('success' in response && response.success) {
          toast.success(
            'Verification email sent! Check your inbox.',
            'Email sent successfully'
          );
          await emailStatus.refetch();
        } else if ('message' in response) {
          toast.error(
            'Failed to resend email',
            response.message || 'Please try again.'
          );
        }
        return;
      }

      toast.error(
        'Verification Failed',
        'Security verification failed. Please try again later.'
      );
    } catch (error: unknown) {
      interface ApiError {
        response?: {
          status?: number;
          data?: { retryAfter?: number };
        };
      }
      const apiError = error as ApiError;

      if (apiError?.response?.status === 429) {
        const retryAfter = apiError.response?.data?.retryAfter || 300;
        toast.error(
          'Rate limit exceeded',
          `Please wait ${Math.floor(retryAfter / 60)} minutes before resending.`,
          { duration: 5000 }
        );
      } else if (apiError?.response?.status === 400) {
        toast.info('Already verified', 'Your email is already verified.');
      } else {
        toast.error('Failed to resend email', 'Please try again.', {
          duration: 4000
        });
      }
    } finally {
      setIsResendingWithCaptcha(false);
    }
  };

  if (!authState.isAuthenticated || authState.user === null) {
    return null;
  }

  const has401Error =
    emailStatus.error &&
    typeof emailStatus.error === 'object' &&
    'statusCode' in emailStatus.error &&
    emailStatus.error.statusCode === 401;

  if (has401Error) return null;
  if (emailStatus.isLoading || emailStatus.isFetching) return null;
  if (emailStatus.error) return null;
  if (emailVerified) return null;
  if (dismissed) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
        className={`w-full border bg-red-50 border-red-200 text-red-800 ${className}`}
      >
        <div className="mx-auto max-w-7xl px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex min-w-0 flex-1 items-center space-x-3">
              <AlertCircle className="h-5 w-5 shrink-0" />
              <p className="truncate text-sm font-medium">
                Verify your email to unlock your account. Check your inbox for
                the verification link.
              </p>
            </div>
            <div className="ml-4 flex shrink-0 items-center space-x-3">
              <button
                onClick={() => {
                  dismissBanner();
                  setDismissed(true);
                }}
                className="rounded-md p-1 transition-colors hover:bg-red-100"
                aria-label="Dismiss banner"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/verify-email')}
              className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-xs transition-colors hover:bg-emerald-700"
            >
              <Mail className="h-4 w-4" />
              Verify Now
            </motion.button>

            {countdown > 0 ? (
              <InteractiveCountdown
                seconds={countdown}
                totalSeconds={initialCountdown || 300}
                attemptsRemaining={emailStatus.data?.attemptsRemaining}
              />
            ) : (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleResendEmail}
                disabled={
                  isResending ||
                  isResendingWithCaptcha ||
                  emailStatus.isLoading ||
                  (emailStatus.data !== undefined &&
                    !emailStatus.data?.canResend)
                }
                className="flex items-center gap-2 rounded-lg bg-gray-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {emailStatus.isLoading ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Loading...
                  </>
                ) : isResending || isResendingWithCaptcha ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4" />
                    Resend Email
                  </>
                )}
              </motion.button>
            )}

            {emailStatus.data &&
              emailStatus.data.attemptsRemaining !== undefined &&
              emailStatus.data.attemptsRemaining < 3 && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={`rounded-full px-2 py-1 text-xs font-medium ${
                    emailStatus.data.attemptsRemaining === 0
                      ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                      : emailStatus.data.attemptsRemaining === 1
                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                  }`}
                >
                  {emailStatus.data.attemptsRemaining} resend
                  {emailStatus.data.attemptsRemaining !== 1 ? 's' : ''} left
                </motion.span>
              )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default VerificationBanner;
